import { Card, Tag } from 'antd';
import type { ArrangementDto } from '@pams/shared';
import { EmptyState } from '@/components/EmptyState';
import { DETAIL_COPY } from '../copy';

type DutyPoint = ArrangementDto['dutyPoints'][number];

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  WHO IS IN COMMAND, PER DUTY POINT. [FIX-P6b.1 · amended P6b.2]
 *
 *  ⚠️ THIS WAS COLLECTED AND NEVER SHOWN BACK. The intake asks for command entries per duty
 *  point, `dutyPointSchema.commandEntries` has carried them on the wire since [D4], and this
 *  page rendered places, policy, force and duties — and nothing at all about supervision. An
 *  operator could name an Incharge and then had no screen on which to read that they had.
 *  Nothing caught it because the detail page's own fixture set `commandEntries: []`.
 *
 *  ⚠️ [amended P6b.2] ONE CARD PER DUTY POINT, NOT ONE PANEL PER ORDER — AND THE EMPTY
 *  SENTENCE IS WHY IT MATTERS. The first version stacked every card's supervision inside one
 *  panel above the duties table and SKIPPED a duty point with none (`length === 0` → `null`).
 *  On a 24-card order that reads as "these are the commanders" while saying nothing about the
 *  twenty cards that have none — the operator cannot tell "no supervision here" from "not
 *  shown here", which is the same defect this component was built to fix, one level down.
 *  Every rendered card now answers for itself.
 *
 *  ⚠️ IT COUNTS NOTHING, AND THAT IS FR-5.2.6b RATHER THAN AN OVERSIGHT. Command roles never
 *  reach a count, a total, an allocation or a fulfilment percentage — the requirement closes
 *  OD-9 because everybody assumes command counts toward force. So this LISTS. There is no
 *  "3 commanders", no share of anything, and no arithmetic on this page's command data at
 *  all. `commandRoleIsolation.test.ts` CLAIM 5 scans `api/src` and `packages/shared/src`
 *  rather than `web/`, so the rule here is held by review — which is exactly why it is
 *  written at the top of the one file that would break it. ⚠️ `dutyPoints.length` below is an
 *  EMPTINESS TEST on the duty points, not a headcount of anything command-shaped; CLAIM 5
 *  permits `.length` only in that form and this file keeps to it.
 *
 *  ⚠️ ONE FIELD TO RENDER, NO BRANCH. `roleLabel` is resolved server-side from the frozen
 *  policy version, or — for [D4.3]'s one-off row, which carries no `roleKey` — from the row's
 *  OWN `adHocLabel`. The schema says so in as many words: "so every reader has one field to
 *  render and none has to branch." A client that fell back to `roleKey` would print a
 *  SCREAMING_SNAKE key beside a human name.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
const CommandEntries = ({ point, emptyTitle }: { point: DutyPoint; emptyTitle: string }) => {
  if (point.commandEntries.length === 0) {
    return <EmptyState size="sm" title={emptyTitle} />;
  }

  return (
    <dl className="divide-y divide-border">
      {point.commandEntries.map((entry, index) => (
        <div
          key={`${point.id}-${entry.roleKey ?? entry.adHocLabel ?? String(index)}`}
          className="flex flex-wrap items-baseline gap-x-3 gap-y-1 py-1.5"
        >
          <dt className="min-w-32 text-sm text-muted-foreground">
            {entry.roleLabel ?? DETAIL_COPY.commandUnnamedRole}
            {/* A one-off row names itself and belongs to this duty point alone. */}
            {entry.roleKey === null && (
              <Tag className="m-0 ml-1.5" color="default">
                {DETAIL_COPY.commandOneOff}
              </Tag>
            )}
          </dt>
          <dd className="min-w-0 flex-1 text-sm">
            {/*
              ⚠️ [C6a] A KNOWN GAP OF THIS HIDDEN PAGE (`SURFACES.arrangementRecordPage`): a role ASKED of a
              division carries its positions in `entry.asks[].seat` and an empty `officers`, so it reads
              `Nobody named` here even once the division has named somebody. The live record page and the
              preview read the seats through `CommandContacts`; flipping this page back on needs the same.
            */}
            {entry.officers.length === 0 ? (
              <span className="text-muted-foreground">{DETAIL_COPY.commandNobody}</span>
            ) : (
              entry.officers
                .map((officer) => (officer.rankShortName ? `${officer.rankShortName} ${officer.name}` : officer.name))
                .join(', ')
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
};

export const CommandRoles = ({ dutyPoints }: { dutyPoints: ArrangementDto['dutyPoints'] }) => {
  /*
   * ⚠️ AN ORDER WITH NO DUTY POINTS AT ALL IS A DRAFT NOBODY HAS FILLED IN, and it gets the
   * order-level sentence rather than a heading over nothing. `commandEntries` is `.optional()`
   * on the way in precisely because an order may carry no supervision by design; rendering
   * nothing at all would be indistinguishable from the defect this component fixes.
   */
  if (dutyPoints.length === 0) {
    return (
      <Card size="small" data-testid="command-roles">
        <div className="mb-2">
          <h2 className="text-sm font-semibold tracking-tight">{DETAIL_COPY.commandTitle}</h2>
          <p className="text-xs text-muted-foreground">{DETAIL_COPY.commandHint}</p>
        </div>
        <EmptyState size="sm" title={DETAIL_COPY.commandEmpty} />
      </Card>
    );
  }

  /*
   * ⚠️ A SINGLE-CARD ORDER RENDERS EXACTLY AS IT DID — one card, one heading, no duty point
   * title. The card title would repeat what the order header one column over already says,
   * and the duties table below takes the same decision on `multiPoint` for the same reason.
   */
  const multiPoint = dutyPoints.length > 1;

  return (
    <div className="space-y-3" data-testid="command-roles">
      {dutyPoints.map((point, index) => (
        <Card key={point.id} size="small" data-testid={`command-point-${point.id}`}>
          {/* The section heading belongs to the FIRST card only — repeating "Command" over
              every card would make one subject read as several. */}
          {index === 0 && (
            <div className="mb-2">
              <h2 className="text-sm font-semibold tracking-tight">{DETAIL_COPY.commandTitle}</h2>
              <p className="text-xs text-muted-foreground">{DETAIL_COPY.commandHint}</p>
            </div>
          )}
          {multiPoint && <p className="mb-1 text-xs font-medium text-muted-foreground">{point.displayTitle}</p>}
          {/* ⚠️ THE SENTENCE MATCHES THE SUBJECT. On a one-card order "this duty point" is a
              distinction the operator never made — the card IS the order — so it says so
              about the ORDER. On a multi-card one the order-level sentence would be a claim
              about the other cards that this card cannot make. */}
          <CommandEntries
            point={point}
            emptyTitle={multiPoint ? DETAIL_COPY.commandPointEmpty : DETAIL_COPY.commandEmpty}
          />
        </Card>
      ))}
    </div>
  );
};
